import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid2,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { effectiveDailyMatchLimit, isValidRuleDate, updateDailyMatchRule } from "./dailyMatchLimits";

export default function DailyMatchLimitsSection({ config, windows, onChange, intl }) {
  const [date, setDate] = useState("");
  const [limit, setLimit] = useState("1");
  const text = (key, values) => intl.formatMessage({ id: `scheduling.daily.${key}` }, values);
  const label = (value) => (value === null ? text("unlimited") : String(value));
  const options = (inherit) => [
    ...(inherit
      ? [
          <MenuItem key="inherit" value="inherit">
            {text("inherit")}
          </MenuItem>,
        ]
      : []),
    <MenuItem key="unlimited" value="unlimited">
      {text("unlimited")}
    </MenuItem>,
    ...Array.from({ length: 10 }, (_, index) => (
      <MenuItem key={index + 1} value={String(index + 1)}>
        {index + 1}
      </MenuItem>
    )),
  ];
  const dates = [...new Set((windows || []).map((windowItem) => windowItem.date.slice(0, 10)))].sort();

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        <Typography variant="subtitle2">{text("priorityTitle")}</Typography>
        <Typography variant="body2">{text("help")}</Typography>
      </Alert>

      <Stack spacing={2}>
        <Typography variant="subtitle2">{text("weekdayOverrides")}</Typography>
        <Grid2 container spacing={1}>
          {Array.from({ length: 7 }, (_, index) => {
            const weekday = index + 1;
            const rule = (config.weekday_match_limits || []).find((item) => Number(item.weekday) === weekday);

            return (
              <Grid2 key={weekday} sx={{ minWidth: 100 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={intl.formatDate(new Date(Date.UTC(2026, 0, 5 + index)), {
                    weekday: "long",
                    timeZone: "UTC",
                  })}
                  value={rule ? (rule.max_matches === null ? "unlimited" : String(rule.max_matches)) : "inherit"}
                  onChange={(event) =>
                    onChange(
                      "weekday_match_limits",
                      updateDailyMatchRule(config.weekday_match_limits, "weekday", weekday, event.target.value),
                    )
                  }
                >
                  {options(true)}
                </TextField>
              </Grid2>
            );
          })}
        </Grid2>
      </Stack>

      <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
        <Typography variant="subtitle2">{text("exceptions")}</Typography>
        <Typography variant="caption" color="text.secondary">
          {text("exceptionsHelp")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
          <TextField
            type="date"
            size="small"
            label={text("date")}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            size="small"
            label={text("limit")}
            value={limit}
            sx={{ minWidth: 180 }}
            onChange={(event) => setLimit(event.target.value)}
          >
            {options(false)}
          </TextField>
          <Button
            variant="outlined"
            disabled={!isValidRuleDate(date)}
            onClick={() => {
              onChange("date_match_limits", updateDailyMatchRule(config.date_match_limits, "date", date, limit));
              setDate("");
            }}
          >
            {text("addException")}
          </Button>
        </Stack>

        {(config.date_match_limits || []).length > 0 && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {(config.date_match_limits || []).map((rule) => (
              <Stack
                key={rule.date}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                <Typography variant="body2" sx={{ minWidth: 90, flexGrow: 1 }}>
                  {rule.date}
                </Typography>
                <TextField
                  select
                  size="small"
                  label={text("limit")}
                  sx={{ minWidth: 160 }}
                  value={rule.max_matches === null ? "unlimited" : String(rule.max_matches)}
                  onChange={(event) =>
                    onChange(
                      "date_match_limits",
                      updateDailyMatchRule(config.date_match_limits, "date", rule.date, event.target.value),
                    )
                  }
                >
                  {options(false)}
                </TextField>
                <Tooltip title={text("remove")}>
                  <IconButton
                    color="error"
                    aria-label={`${text("remove")} ${rule.date}`}
                    onClick={() =>
                      onChange(
                        "date_match_limits",
                        updateDailyMatchRule(config.date_match_limits, "date", rule.date, "inherit"),
                      )
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
